import React, { useState, useEffect, useMemo, useRef } from "react";
import { Request } from "../../hooks/Request";
import { validateType } from "../../utils/constant";
import { validateArray, validateStatus } from "../../utils/validate";
import { requestAuth } from "../../components/services/RequestService";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { Formik, useFormikContext } from "formik";
import * as YupValidation from "yup";
import CreatableSelect from "react-select/creatable";
import {
  adjuntarDex,
  readDam,
  readDim,
  readParte,
} from "../../components/services/ReadFile";
import { useModal } from "../../contexts/ModalContext";

/**
 * ESTRUCTURA DE RUTAS (según Excel DETALLE_DIAS_DE_TRANSITO):
 *
 * MULTIMODAL  embarque ──(marítimos)──► descarga ──(terrestres)──► destino final
 * MARITIMO    embarque ──(marítimos)──► descarga
 * TERRESTRE   embarque ──(terrestres)────────────────────────────► destino final
 * AEREO       embarque ──(aéreos)────► arribo ──(terrestres)────► destino final
 */

const listFields = [
  "a13ParteModalidadDeTransporte",
  "paísDeOrigen",
  "puertoOCiudadDeEmbarque",
  "paísDeDescarga",
  "puertoDeDescarga",
  "aeropuertoDeArribo",
  "paísDestinoFinal",
  "ciudadDestinoFinal",
  "díasMarítimos",
  "díasTerrestres",
  "díasAéreos",
  "totalTransitoCalculado",
  "etd",
  "eta",
  "fechaDevoluciónContenedores",
  "fechaDeLlegadaAAlmacen",
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const normalizeStr = (str) => {
  if (!str) return "";
  return str
    .toString()
    .toUpperCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

const modeConfig = {
  MARITIMO: { hasDischarge: true, hasArribo: false, hasDestino: false },
  MULTIMODAL: { hasDischarge: true, hasArribo: false, hasDestino: true },
  TERRESTRE: { hasDischarge: false, hasArribo: false, hasDestino: true },
  AEREO: { hasDischarge: false, hasArribo: true, hasDestino: true },
};

// ─── Styled Components ────────────────────────────────────────────────────────

/**
 * FIX removeChild: menuPortalTarget={document.body} + menuPosition="fixed"
 * Hace que el dropdown de react-select se monte en <body> en lugar de dentro
 * del contenedor del componente. Así, cuando ocultamos con display:none,
 * el portal no intenta remover un nodo que ya no está en el árbol del padre.
 */
const CreatableSelectField = ({
  label,
  options,
  value,
  onChange,
  disabled,
  catalogDefault,
}) => {
  const selectOptions =
    options?.map((opt) => ({ value: opt.value, label: opt.label })) || [];
  const selectedOption =
    selectOptions.find(
      (opt) => normalizeStr(opt.value) === normalizeStr(value),
    ) || (value ? { value, label: value } : null);

  const showNote =
    catalogDefault && normalizeStr(catalogDefault) !== normalizeStr(value);

  return (
    <div className="flex flex-col mb-4">
      <label className="text-sm font-semibold text-gray-700 mb-1">
        {label}
      </label>
      <CreatableSelect
        isSearchable
        isClearable
        isDisabled={disabled}
        placeholder="Seleccionar o escribir nuevo..."
        options={selectOptions}
        value={selectedOption}
        onChange={(v) => onChange(v ? v.value : "")}
        formatCreateLabel={(v) => `Crear: "${v}"`}
        // FIX: renderiza el menú en document.body para evitar removeChild
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : null
        }
        menuPosition="fixed"
        styles={{
          control: (b) => ({
            ...b,
            borderRadius: "0.75rem",
            padding: "2px",
            borderColor: "#E5E7EB",
            boxShadow: "none",
            "&:hover": { borderColor: "#3B82F6" },
          }),
          menu: (b) => ({
            ...b,
            borderRadius: "0.75rem",
            overflow: "hidden",
            zIndex: 9999,
          }),
          menuPortal: (b) => ({ ...b, zIndex: 9999 }),
          input: (b) => ({ ...b, textTransform: "uppercase" }),
          singleValue: (b) => ({ ...b, textTransform: "uppercase" }),
          option: (b) => ({ ...b, textTransform: "uppercase" }),
        }}
      />
      {showNote && (
        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
          <span>⚠</span> Valor del catálogo:{" "}
          <span className="font-medium">{catalogDefault}</span>
        </p>
      )}
    </div>
  );
};

const StyledSelect = ({ label, name, options, value, onChange }) => (
  <div className="flex flex-col mb-4">
    <label className="text-sm font-semibold text-gray-700 mb-1">{label}</label>
    <select
      name={name}
      value={value || ""}
      onChange={onChange}
      className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-gray-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 shadow-sm appearance-none cursor-pointer text-sm uppercase"
      style={{
        backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%234B5563' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
        backgroundPosition: "right 1rem center",
        backgroundSize: "1.25em",
        backgroundRepeat: "no-repeat",
        paddingRight: "2.5rem",
      }}
    >
      <option value="">Seleccionar...</option>
      {options?.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

const StyledInput = ({
  label,
  name,
  type,
  value,
  onChange,
  disabled,
  placeholder,
  catalogDefault,
}) => {
  const showNote =
    catalogDefault !== undefined &&
    catalogDefault !== null &&
    catalogDefault !== "" &&
    String(catalogDefault) !== String(value);
  return (
    <div className="flex flex-col mb-4">
      <label className="text-sm font-semibold text-gray-700 mb-1">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value || ""}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        className={`w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-gray-800 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 disabled:bg-gray-100 disabled:text-gray-400 shadow-sm ${
          type === "date" || type === "number" ? "" : "uppercase"
        }`}
      />
      {showNote && (
        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
          <span>⚠</span> Valor del catálogo:{" "}
          <span className="font-medium">{catalogDefault}</span>
        </p>
      )}
    </div>
  );
};

const StyledCheckbox = ({ label, name, checked, onChange }) => (
  <label className="flex items-center gap-3 bg-blue-50/50 hover:bg-blue-50 border border-blue-100 rounded-xl p-4 cursor-pointer transition-all duration-200 select-none shadow-sm mb-4">
    <input
      type="checkbox"
      name={name}
      checked={checked || false}
      onChange={onChange}
      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
    />
    <span className="text-sm font-medium text-blue-900">{label}</span>
  </label>
);

// ─────────────────────────────────────────────────────────────────────────────
// FormStateCoordinator — FUERA del componente padre para evitar remounts.
//
// Prioridad de valores:
//   1. dataSets[0].value  (lo que ya guardó el usuario en el trámite)
//   2. initial            (valor por defecto del campo)
//   3. catálogo           (solo si los campos de días están vacíos)
// ─────────────────────────────────────────────────────────────────────────────
const FormStateCoordinator = ({
  transitCatalog,
  loadingCatalog,
  catalogDefaults,
}) => {
  const { values, setFieldValue } = useFormikContext();

  const mode = useMemo(
    () => normalizeStr(values.a13ParteModalidadDeTransporte),
    [values.a13ParteModalidadDeTransporte],
  );
  const origin = values.puertoOCiudadDeEmbarque;
  const discharge = values.puertoDeDescarga;
  const arribo = values.aeropuertoDeArribo;
  const finalDest = values.ciudadDestinoFinal;
  const etd = values.etd;

  // Controla si ya inicializamos desde dataSets (no se debe sobreescribir con catálogo)
  const prevRouteRef = useRef("");
  const isFirstRunRef = useRef(true);

  // 1. Buscar en catálogo SOLO si los campos de días están vacíos
  useEffect(() => {
    if (!mode || loadingCatalog) return;
    const routeKey = `${mode}-${origin}-${discharge}-${arribo}-${finalDest}`;
    if (prevRouteRef.current === routeKey) return;
    prevRouteRef.current = routeKey;

    // En el primer render, si el trámite ya tiene días guardados → NO sobreescribir
    if (isFirstRunRef.current) {
      isFirstRunRef.current = false;
      const hasSaved =
        values.díasAéreos ||
        values.díasMarítimos ||
        values.díasTerrestres ||
        values.totalTransitoCalculado;
      if (hasSaved) return;
    }

    // Buscar coincidencia en catálogo
    let matched = null;
    for (const opt of transitCatalog) {
      try {
        const parsed = JSON.parse(opt.data);
        if (normalizeStr(parsed.transport_mode) !== mode) continue;
        const segs = parsed.segments || [];
        if (mode === "MARITIMO") {
          if (
            segs.some(
              (s) =>
                s.type === "MARITIMO" &&
                normalizeStr(s.from) === normalizeStr(origin) &&
                normalizeStr(s.to) === normalizeStr(discharge),
            )
          ) {
            matched = parsed;
            break;
          }
        } else if (mode === "TERRESTRE") {
          if (
            segs.some(
              (s) =>
                s.type === "TERRESTRE" &&
                normalizeStr(s.from) === normalizeStr(origin) &&
                normalizeStr(s.to) === normalizeStr(finalDest),
            )
          ) {
            matched = parsed;
            break;
          }
        } else if (mode === "AEREO") {
          const hasAir = segs.some(
            (s) =>
              s.type === "AEREO" &&
              normalizeStr(s.from) === normalizeStr(origin) &&
              normalizeStr(s.to) === normalizeStr(arribo),
          );
          const hasLand = segs.some(
            (s) =>
              s.type === "TERRESTRE" &&
              normalizeStr(s.from) === normalizeStr(arribo) &&
              normalizeStr(s.to) === normalizeStr(finalDest),
          );
          if (hasAir && hasLand) {
            matched = parsed;
            break;
          }
        } else if (mode === "MULTIMODAL") {
          const firstFrom = segs[0]?.from;
          const lastTo = segs[segs.length - 1]?.to;
          const hasStop = segs.some(
            (s) =>
              normalizeStr(s.to) === normalizeStr(discharge) ||
              normalizeStr(s.from) === normalizeStr(discharge),
          );
          if (
            normalizeStr(firstFrom) === normalizeStr(origin) &&
            normalizeStr(lastTo) === normalizeStr(finalDest) &&
            hasStop
          ) {
            matched = parsed;
            break;
          }
        }
      } catch {
        /* skip */
      }
    }

    let maritime = 0,
      land = 0,
      air = 0;
    if (matched) {
      matched.segments?.forEach((s) => {
        if (s.type === "MARITIMO") maritime += Number(s.transit_days || 0);
        if (s.type === "TERRESTRE") land += Number(s.transit_days || 0);
        if (s.type === "AEREO") air += Number(s.transit_days || 0);
      });
    }
    setFieldValue("díasMarítimos", matched ? maritime || "" : "");
    setFieldValue("díasTerrestres", matched ? land || "" : "");
    setFieldValue("díasAéreos", matched ? air || "" : "");
    setFieldValue(
      "totalTransitoCalculado",
      matched ? maritime + land + air || "" : "",
    );
  }, [
    mode,
    origin,
    discharge,
    arribo,
    finalDest,
    transitCatalog,
    loadingCatalog,
  ]);

  // 2. Recalcular total cuando el usuario edita días manualmente
  useEffect(() => {
    const total =
      Number(values.díasMarítimos || 0) +
      Number(values.díasTerrestres || 0) +
      Number(values.díasAéreos || 0);
    if (Number(values.totalTransitoCalculado) !== total)
      setFieldValue("totalTransitoCalculado", total || "");
  }, [values.díasMarítimos, values.díasTerrestres, values.díasAéreos]);

  // 3. Recalcular fechas
  useEffect(() => {
    const maritime = Number(values.díasMarítimos || 0);
    const land = Number(values.díasTerrestres || 0);
    const air = Number(values.díasAéreos || 0);

    if (etd && dayjs(etd).isValid()) {
      if (mode === "MARITIMO" || mode === "MULTIMODAL") {
        setFieldValue(
          "eta",
          dayjs(etd).add(maritime, "day").format("YYYY-MM-DD"),
        );
      } else if (mode === "AEREO") {
        setFieldValue("eta", dayjs(etd).add(air, "day").format("YYYY-MM-DD"));
      } else {
        setFieldValue("eta", "");
      }
    } else {
      setFieldValue("eta", "");
    }

    const etaVal = values.eta;
    if (mode === "TERRESTRE") {
      setFieldValue(
        "fechaDeLlegadaAAlmacen",
        etd && dayjs(etd).isValid()
          ? dayjs(etd).add(land, "day").format("YYYY-MM-DD")
          : "",
      );
    } else if (mode === "MULTIMODAL" || mode === "AEREO") {
      setFieldValue(
        "fechaDeLlegadaAAlmacen",
        etaVal && dayjs(etaVal).isValid()
          ? dayjs(etaVal).add(land, "day").format("YYYY-MM-DD")
          : "",
      );
    } else {
      setFieldValue("fechaDeLlegadaAAlmacen", "");
    }
  }, [
    mode,
    etd,
    values.eta,
    values.díasMarítimos,
    values.díasTerrestres,
    values.díasAéreos,
  ]);

  useEffect(() => {
    const containerReturnDate =
      values.eta && dayjs(values.eta).isValid()
        ? dayjs(values.eta).add(21, "day").format("YYYY-MM-DD")
        : "";

    if (values.fechaDevoluciónContenedores !== containerReturnDate) {
      setFieldValue("fechaDevoluciónContenedores", containerReturnDate);
    }
  }, [values.eta, values.fechaDevoluciónContenedores]);

  return null;
};

// ─── Componente principal ─────────────────────────────────────────────────────

export const FormAdditionalInformation = (props) => {
  const { procedure } = props;
  const { onClose } = useModal();

  const [activeStep, setActiveStep] = useState(1);
  const [transitCatalog, setTransitCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);

  const [allModalidades, setAllModalidades] = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  const [allPorts, setAllPorts] = useState([]);
  const [allAirports, setAllAirports] = useState([]);
  const [allCities, setAllCities] = useState([]);

  // formValues: valores inicializados desde dataSets (prioritarios) o initial
  const [formValues, setFormValues] = useState({});
  // catalogDefaults: valores del catálogo por nombre de campo (para mostrar nota)
  const [catalogDefaults, setCatalogDefaults] = useState({});

  const { data, loading } = Request({
    urlApi: `/Procedure/${procedure.id || procedure.procedureId}/datasets?${listFields.map((f) => `fieldnames=${f}`).join("&")}`,
  });

  // ── Catálogos ───────────────────────────────────────────────────────────────

  async function fetchDropdownData() {
    const [modalRes, countryRes, portRes, airportRes, cityRes] =
      await Promise.all([
        requestAuth("get", "/Dropdown/modalidadDespacho/options").catch(
          () => null,
        ),
        requestAuth("get", "/Dropdown/country/options").catch(() => null),
        requestAuth("get", "/Dropdown/port/options").catch(() => null),
        requestAuth("get", "/Dropdown/city/options").catch(() => null),
        requestAuth("get", "/Dropdown/city/options").catch(() => null),
      ]);
    setAllModalidades(modalRes?.data || []);
    setAllCountries(countryRes?.data || []);
    setAllPorts(portRes?.data || []);
    setAllAirports(airportRes?.data || []);
    setAllCities(cityRes?.data || []);
    return {
      modalities: modalRes?.data || [],
      countries: countryRes?.data || [],
      ports: portRes?.data || [],
      airports: airportRes?.data || [],
      cities: cityRes?.data || [],
    };
  }

  useEffect(() => {
    (async () => {
      try {
        setLoadingCatalog(true);
        await fetchDropdownData();
        const res = await requestAuth(
          "get",
          "/Dropdown/transit-days/options?PageNumber=1",
        ).catch(() => null);
        setTransitCatalog(res?.data || []);
      } finally {
        setLoadingCatalog(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (data) setFormValues(dataInitialValues(data));
  }, [data]);

  // ── Helpers de datos ────────────────────────────────────────────────────────

  function dataFields(list) {
    let aux = {};
    list?.forEach((el) => {
      aux[el.name] = el.validate ? validateType[el.type] || null : null;
    });
    return aux;
  }

  /**
   * Prioridad de valores:
   *   1. dataSets[0].value  → ya guardado en el trámite
   *   2. initial            → valor por defecto del campo en BD
   *   3. ""                 → vacío
   */
  function dataInitialValues(list) {
    let aux = {};
    list?.forEach((el) => {
      const saved = el.dataSets?.[0]?.value;
      const initial = el.initial === "false" ? false : el.initial || "";
      aux[el.name] =
        saved !== undefined && saved !== null && saved !== "" ? saved : initial;
    });
    aux.guardarEnCatalogoTransito = false;
    return aux;
  }

  // ── Guardar opciones nuevas ─────────────────────────────────────────────────

  async function ensureOptionsExist(values) {
    const mode = normalizeStr(values.a13ParteModalidadDeTransporte);
    const cfg = modeConfig[mode] || {};

    let currentCountries = [...allCountries];
    let currentPorts = [...allPorts];
    let currentAirports = [...allAirports];
    let currentCities = [...allCities];

    async function checkAndCreate(name, listLabel, countryData = "") {
      if (!name) return;
      const normalizedName = normalizeStr(name);
      const normalizedData = normalizeStr(countryData);

      let exists = false;
      if (listLabel === "country") {
        exists = currentCountries.some(
          (c) => normalizeStr(c.name) === normalizedName,
        );
        if (!exists) {
          currentCountries.push({ name: name.toUpperCase(), data: "" });
        }
      } else if (listLabel === "port") {
        exists = currentPorts.some(
          (p) =>
            normalizeStr(p.name) === normalizedName &&
            (!countryData || normalizeStr(p.data) === normalizedData),
        );
        if (!exists) {
          currentPorts.push({
            name: name.toUpperCase(),
            data: countryData.toUpperCase(),
          });
        }
      } else if (listLabel === "city") {
        exists = currentAirports.some(
          (a) =>
            normalizeStr(a.name) === normalizedName &&
            (!countryData || normalizeStr(a.data) === normalizedData),
        );
        if (!exists) {
          currentAirports.push({
            name: name.toUpperCase(),
            data: countryData.toUpperCase(),
          });
        }
      } else if (listLabel === "city") {
        exists = currentCities.some(
          (c) =>
            normalizeStr(c.name) === normalizedName &&
            (!countryData || normalizeStr(c.data) === normalizedData),
        );
        if (!exists) {
          currentCities.push({
            name: name.toUpperCase(),
            data: countryData.toUpperCase(),
          });
        }
      }

      if (exists) return;

      await requestAuth("post", "/Dropdown/Option", {
        name: name.toUpperCase(),
        type: "Texto",
        data: countryData.toUpperCase(),
        DropdownListLabel: listLabel,
      }).catch(() => {});
    }

    await checkAndCreate(values.paísDeOrigen, "country");
    if (cfg.hasDischarge)
      await checkAndCreate(values.paísDeDescarga, "country");
    if (cfg.hasDestino)
      await checkAndCreate(values.paísDestinoFinal, "country");
    if (mode === "AEREO") {
      await checkAndCreate(
        values.puertoOCiudadDeEmbarque,
        "city",
        values.paísDeOrigen,
      );
      await checkAndCreate(
        values.aeropuertoDeArribo,
        "city",
        values.paísDestinoFinal,
      );
    } else if (mode === "TERRESTRE") {
      await checkAndCreate(
        values.puertoOCiudadDeEmbarque,
        "city",
        values.paísDeOrigen,
      );
    } else {
      await checkAndCreate(
        values.puertoOCiudadDeEmbarque,
        "port",
        values.paísDeOrigen,
      );
    }
    if (cfg.hasDischarge)
      await checkAndCreate(
        values.puertoDeDescarga,
        "port",
        values.paísDeDescarga,
      );
    if (cfg.hasDestino)
      await checkAndCreate(
        values.ciudadDestinoFinal,
        "city",
        values.paísDestinoFinal,
      );

    await fetchDropdownData();
  }

  // ── Catálogo de tránsito ────────────────────────────────────────────────────

  async function handleCatalogSave(values) {
    if (!values.guardarEnCatalogoTransito) return;
    const mode = normalizeStr(values.a13ParteModalidadDeTransporte);
    const origin = values.puertoOCiudadDeEmbarque;
    const discharge = values.puertoDeDescarga;
    const arribo = values.aeropuertoDeArribo;
    const finalDest = values.ciudadDestinoFinal;
    const maritime = Number(values.díasMarítimos || 0);
    const land = Number(values.díasTerrestres || 0);
    const air = Number(values.díasAéreos || 0);

    const segments = [];
    if (["MARITIMO", "MULTIMODAL"].includes(mode) && maritime > 0)
      segments.push({
        type: "MARITIMO",
        from: origin,
        to: discharge || finalDest,
        transit_days: maritime,
      });
    if (["TERRESTRE", "MULTIMODAL"].includes(mode) && land > 0)
      segments.push({
        type: "TERRESTRE",
        from: discharge || origin,
        to: finalDest,
        transit_days: land,
      });
    if (mode === "AEREO") {
      if (air > 0)
        segments.push({
          type: "AEREO",
          from: origin,
          to: arribo,
          transit_days: air,
        });
      if (land > 0)
        segments.push({
          type: "TERRESTRE",
          from: arribo,
          to: finalDest,
          transit_days: land,
        });
    }

    const payload = { transport_mode: mode, segments };
    const middle =
      mode === "AEREO"
        ? arribo
        : ["MARITIMO", "MULTIMODAL"].includes(mode)
          ? discharge
          : "";
    const optionName = [origin, middle, finalDest].filter(Boolean).join("-");

    const existing = transitCatalog.find((opt) => {
      try {
        const p = JSON.parse(opt.data);
        return (
          normalizeStr(opt.name) === normalizeStr(optionName) &&
          normalizeStr(p.transport_mode) === mode
        );
      } catch {
        return false;
      }
    });

    try {
      if (existing) {
        await requestAuth("put", `/Dropdown/Option/${existing.id}`, {
          name: optionName,
          type: "Texto",
          data: JSON.stringify(payload),
          DropdownListLabel: "transit-days",
        });
        toast.info("Catálogo de tránsito actualizado");
      } else {
        await requestAuth("post", "/Dropdown/Option", {
          name: optionName,
          type: "Texto",
          data: JSON.stringify(payload),
          DropdownListLabel: "transit-days",
        });
        toast.success("Nueva ruta agregada al catálogo de tránsito");
      }
      const refetch = await requestAuth(
        "get",
        "/Dropdown/transit-days/options?PageNumber=1",
      ).catch(() => null);
      if (refetch) setTransitCatalog(refetch.data || []);
    } catch (e) {
      console.error("Error saving catalog:", e);
    }
  }

  // ── Submit ──────────────────────────────────────────────────────────────────

  async function submitDataset(rows, resetForm) {
    const filtered = rows.filter((r) => r.name !== "guardarEnCatalogoTransito");
    await requestAuth("post", "/dataset", filtered)
      .then(async () => {
        const listRead = filtered.filter((f) => f.type === "fileRead");
        if (listRead?.length > 0) {
          await readDataProcedure(0, listRead, [], resetForm);
        } else {
          toast.success("Envio exitoso");
        }
        onClose();
      })
      .catch(() => toast.error("Error en el envio"));
  }

  async function readDataProcedure(index, fields, result, resetForm) {
    if (index < fields.length) {
      const cb = async (i, f, r) => readDataProcedure(i, f, r, resetForm);
      switch (fields[index].url) {
        case "adjuntarDim":
        case "dimRegularizada":
          await readDim(fields, index, procedure, result, cb);
          break;
        case "adjuntarDex":
          await adjuntarDex(fields, index, procedure, result, cb);
          break;
        case "documentoDam":
          await readDam(fields, index, procedure, result, cb);
          break;
        case "parteDeRecepcion":
          await readParte(fields, index, procedure, result, cb);
          break;
      }
    } else {
      const bad = result.filter(
        (r) => !validateStatus(r.readStatus) && r.validate,
      );
      validateArray(bad)
        ? toast.error(bad[0].readData)
        : toast.success("Envio exitoso");
    }
  }

  async function handleFinalSubmit(values, resetForm) {
    await ensureOptionsExist(values);
    await handleCatalogSave(values);
    const rows = data.map((el) => ({
      Value:
        values[el.name] !== undefined && values[el.name] !== null
          ? values[el.name]
          : "",
      ProcedureId: procedure.id,
      FieldId: el.id,
      name: el.name,
      url: el.url,
      type: el.type,
    }));
    await submitDataset(rows, resetForm);
  }

  // ── Wizard ──────────────────────────────────────────────────────────────────

  const TOTAL_STEPS = 4;
  const getStepTitle = (s) =>
    [
      "",
      "Ruta Logística",
      "Fecha de Salida (ETD)",
      "Días de Tránsito",
      "Resumen",
    ][s] || "";

  if (!loading || Object.keys(formValues).length === 0) return null;

  // Valores por defecto del catálogo (initial) para mostrar nota comparativa
  const initialDefaults = {};
  data?.forEach((el) => {
    initialDefaults[el.name] = el.initial;
  });

  return (
    <div className="p-6 bg-white/85 rounded-3xl shadow-2xl border border-gray-100/50 backdrop-blur-md max-w-4xl mx-auto">
      {/* ── Stepper ── */}
      <div className="mb-8">
        <div className="flex justify-between items-center relative">
          <div className="absolute left-0 right-0 top-1/2 h-1 bg-gray-200 -translate-y-1/2 z-0 rounded">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-500 rounded"
              style={{
                width: `${((activeStep - 1) / (TOTAL_STEPS - 1)) * 100}%`,
              }}
            />
          </div>
          {[1, 2, 3, 4].map((step) => {
            const done = activeStep > step;
            const active = activeStep === step;
            return (
              <button
                key={step}
                type="button"
                onClick={() => {
                  if (step < activeStep || done) setActiveStep(step);
                }}
                className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300 font-bold text-sm ${active ? "bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-200 scale-110" : done ? "bg-green-500 text-white border-green-500" : "bg-white text-gray-400 border-gray-300 hover:border-gray-400"}`}
              >
                {done ? (
                  <span key="check">
                    <i className="fas fa-check" />
                  </span>
                ) : (
                  <span key="step">{step}</span>
                )}
                <span className="absolute top-14 text-xs font-semibold whitespace-nowrap text-gray-600 hidden md:block">
                  {getStepTitle(step)}
                </span>
              </button>
            );
          })}
        </div>
        <div className="h-6 md:h-0" />
      </div>

      <Formik
        initialValues={formValues}
        validationSchema={YupValidation.object().shape(dataFields(data))}
        onSubmit={async (values, { resetForm }) => {
          if (activeStep < TOTAL_STEPS) {
            setActiveStep((s) => s + 1);
          } else {
            await handleFinalSubmit(values, resetForm);
          }
        }}
      >
        {({ values, handleChange, setFieldValue, submitForm }) => {
          const mode = normalizeStr(values.a13ParteModalidadDeTransporte);
          const cfg = modeConfig[mode] || {};

          const filterByCountry = (list, country) =>
            list
              .filter(
                (i) =>
                  !country || normalizeStr(i.data) === normalizeStr(country),
              )
              .map((i) => ({ value: i.name, label: i.name }));

          const departureOptions =
            mode === "AEREO"
              ? filterByCountry(allAirports, values.paísDeOrigen)
              : mode === "TERRESTRE"
                ? filterByCountry(allCities, values.paísDeOrigen)
                : filterByCountry(allPorts, values.paísDeOrigen);

          const embarqueLabel =
            mode === "AEREO"
              ? "Aeropuerto de embarque"
              : mode === "TERRESTRE"
                ? "Ciudad / lugar de embarque"
                : "Puerto o ciudad de embarque";

          const onModeChange = (e) => {
            handleChange(e);
            [
              "puertoOCiudadDeEmbarque",
              "paísDeDescarga",
              "puertoDeDescarga",
              "aeropuertoDeArribo",
              "paísDestinoFinal",
              "ciudadDestinoFinal",
            ].forEach((f) => setFieldValue(f, ""));
          };

          return (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitForm();
              }}
              className="flex flex-col mt-6"
            >
              {/* Fuera del padre → stable reference, no remount */}
              <FormStateCoordinator
                transitCatalog={transitCatalog}
                loadingCatalog={loadingCatalog}
                catalogDefaults={initialDefaults}
              />

              <div className="bg-gray-50/50 border border-gray-100 rounded-2xl p-6 min-h-[300px] transition-all duration-300">
                <h3 className="text-lg font-bold text-gray-800 mb-6 border-b border-gray-200 pb-2">
                  {getStepTitle(activeStep)}
                </h3>

                {/* ── PASO 1: Ruta ── */}
                <div style={{ display: activeStep === 1 ? "block" : "none" }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                    <StyledSelect
                      label="Modalidad de transporte"
                      name="a13ParteModalidadDeTransporte"
                      options={allModalidades.map((m) => ({
                        value: m.name,
                        label: m.name,
                      }))}
                      value={values.a13ParteModalidadDeTransporte}
                      onChange={onModeChange}
                    />

                    <CreatableSelectField
                      label="País de origen"
                      options={allCountries.map((c) => ({
                        value: c.name,
                        label: c.name,
                      }))}
                      value={values.paísDeOrigen}
                      catalogDefault={initialDefaults.paísDeOrigen}
                      onChange={(val) => {
                        setFieldValue("paísDeOrigen", val);
                        setFieldValue("puertoOCiudadDeEmbarque", "");
                      }}
                    />

                    <CreatableSelectField
                      label={embarqueLabel}
                      options={departureOptions}
                      value={values.puertoOCiudadDeEmbarque}
                      catalogDefault={initialDefaults.puertoOCiudadDeEmbarque}
                      onChange={(val) =>
                        setFieldValue("puertoOCiudadDeEmbarque", val)
                      }
                    />

                    {/* MARITIMO / MULTIMODAL: puerto de descarga */}
                    <div
                      className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6"
                      style={{ display: cfg.hasDischarge ? "grid" : "none" }}
                    >
                      <CreatableSelectField
                        label="País de descarga"
                        options={allCountries.map((c) => ({
                          value: c.name,
                          label: c.name,
                        }))}
                        value={values.paísDeDescarga}
                        catalogDefault={initialDefaults.paísDeDescarga}
                        onChange={(val) => {
                          setFieldValue("paísDeDescarga", val);
                          setFieldValue("puertoDeDescarga", "");
                        }}
                      />
                      <CreatableSelectField
                        label="Puerto de descarga"
                        options={filterByCountry(
                          allPorts,
                          values.paísDeDescarga,
                        )}
                        value={values.puertoDeDescarga}
                        catalogDefault={initialDefaults.puertoDeDescarga}
                        onChange={(val) =>
                          setFieldValue("puertoDeDescarga", val)
                        }
                      />
                    </div>

                    {/* AEREO: aeropuerto de arribo */}
                    <div
                      className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6"
                      style={{ display: cfg.hasArribo ? "grid" : "none" }}
                    >
                      <CreatableSelectField
                        label="País de arribo"
                        options={allCountries.map((c) => ({
                          value: c.name,
                          label: c.name,
                        }))}
                        value={values.paísDestinoFinal}
                        catalogDefault={initialDefaults.paísDestinoFinal}
                        onChange={(val) => {
                          setFieldValue("paísDestinoFinal", val);
                          setFieldValue("aeropuertoDeArribo", "");
                          setFieldValue("ciudadDestinoFinal", "");
                        }}
                      />
                      <CreatableSelectField
                        label="Aeropuerto de arribo"
                        options={filterByCountry(
                          allAirports,
                          values.paísDestinoFinal,
                        )}
                        value={values.aeropuertoDeArribo}
                        catalogDefault={initialDefaults.aeropuertoDeArribo}
                        onChange={(val) =>
                          setFieldValue("aeropuertoDeArribo", val)
                        }
                      />
                    </div>

                    {/* MULTIMODAL / TERRESTRE / AEREO: ciudad destino final */}
                    <div
                      className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6"
                      style={{ display: cfg.hasDestino ? "grid" : "none" }}
                    >
                      <div
                        style={{ display: cfg.hasArribo ? "none" : "block" }}
                      >
                        <CreatableSelectField
                          label="País destino final"
                          options={allCountries.map((c) => ({
                            value: c.name,
                            label: c.name,
                          }))}
                          value={values.paísDestinoFinal}
                          catalogDefault={initialDefaults.paísDestinoFinal}
                          onChange={(val) => {
                            setFieldValue("paísDestinoFinal", val);
                            setFieldValue("ciudadDestinoFinal", "");
                          }}
                        />
                      </div>
                      <CreatableSelectField
                        label="Ciudad destino final"
                        options={filterByCountry(
                          allCities,
                          values.paísDestinoFinal,
                        )}
                        value={values.ciudadDestinoFinal}
                        catalogDefault={initialDefaults.ciudadDestinoFinal}
                        onChange={(val) =>
                          setFieldValue("ciudadDestinoFinal", val)
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* ── PASO 2: ETD ── */}
                <div style={{ display: activeStep === 2 ? "block" : "none" }}>
                  <div className="max-w-md mx-auto">
                    <StyledInput
                      label="Fecha Tentativa Salida (ETD)"
                      name="etd"
                      type="date"
                      value={values.etd}
                      onChange={handleChange}
                      catalogDefault={
                        initialDefaults.etd !== values.etd
                          ? initialDefaults.etd
                          : undefined
                      }
                    />
                  </div>
                </div>

                {/* ── PASO 3: Días de tránsito ── */}
                <div style={{ display: activeStep === 3 ? "block" : "none" }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                    <div
                      style={{
                        display: ["MARITIMO", "MULTIMODAL"].includes(mode)
                          ? "block"
                          : "none",
                      }}
                    >
                      <StyledInput
                        label="Días marítimos"
                        name="díasMarítimos"
                        type="number"
                        value={values.díasMarítimos}
                        onChange={handleChange}
                        placeholder="Ingrese días"
                        catalogDefault={initialDefaults.díasMarítimos}
                      />
                    </div>

                    <div
                      style={{ display: mode === "AEREO" ? "block" : "none" }}
                    >
                      <StyledInput
                        label="Días aéreos (embarque → aeropuerto arribo)"
                        name="díasAéreos"
                        type="number"
                        value={values.díasAéreos}
                        onChange={handleChange}
                        placeholder="Ingrese días"
                        catalogDefault={initialDefaults.díasAéreos}
                      />
                    </div>

                    <div
                      style={{
                        display: ["TERRESTRE", "MULTIMODAL", "AEREO"].includes(
                          mode,
                        )
                          ? "block"
                          : "none",
                      }}
                    >
                      <StyledInput
                        label={
                          mode === "AEREO"
                            ? "Días terrestres (aeropuerto arribo → destino final)"
                            : "Días terrestres"
                        }
                        name="díasTerrestres"
                        type="number"
                        value={values.díasTerrestres}
                        onChange={handleChange}
                        placeholder="Ingrese días"
                        catalogDefault={initialDefaults.díasTerrestres}
                      />
                    </div>

                    <StyledInput
                      label="Total tránsito calculado"
                      name="totalTransitoCalculado"
                      type="number"
                      value={values.totalTransitoCalculado}
                      disabled
                    />

                    <div className="md:col-span-2 mt-4">
                      <StyledCheckbox
                        label="Actualizar o agregar esta ruta al catálogo de tránsito"
                        name="guardarEnCatalogoTransito"
                        checked={values.guardarEnCatalogoTransito}
                        onChange={(e) =>
                          setFieldValue(
                            "guardarEnCatalogoTransito",
                            e.target.checked,
                          )
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* ── PASO 4: Resumen completo ── */}
                <div style={{ display: activeStep === 4 ? "block" : "none" }}>
                  <div className="space-y-4">
                    {/* Fechas calculadas */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                      <div
                        style={{
                          display: ["MARITIMO", "MULTIMODAL", "AEREO"].includes(
                            mode,
                          )
                            ? "block"
                            : "none",
                        }}
                      >
                        <StyledInput
                          label={
                            mode === "AEREO"
                              ? "Fecha Arribo a Aeropuerto (ETA)"
                              : "Fecha Arribo a Puerto (ETA)"
                          }
                          name="eta"
                          type="date"
                          value={values.eta}
                          disabled
                        />
                      </div>
                      <div
                        style={{
                          display: [
                            "TERRESTRE",
                            "MULTIMODAL",
                            "AEREO",
                          ].includes(mode)
                            ? "block"
                            : "none",
                        }}
                      >
                        <StyledInput
                          label="Fecha de llegada a Almacén"
                          name="fechaDeLlegadaAAlmacen"
                          type="date"
                          value={values.fechaDeLlegadaAAlmacen}
                          disabled
                        />
                      </div>
                    </div>

                    {/* ── Resumen de ruta completo ── */}
                    <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 text-sm">
                      <p className="font-bold text-blue-900 mb-3 text-base flex items-center gap-2">
                        <span>📋</span> Resumen de ruta
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-blue-800">
                        {/* Modalidad */}
                        <div className="flex justify-between bg-white/70 rounded-lg px-3 py-2">
                          <span className="text-gray-500">Modalidad</span>
                          <span className="font-semibold">
                            {values.a13ParteModalidadDeTransporte || "—"}
                          </span>
                        </div>

                        {/* ETD */}
                        <div className="flex justify-between bg-white/70 rounded-lg px-3 py-2">
                          <span className="text-gray-500">ETD (salida)</span>
                          <span className="font-semibold">
                            {values.etd || "—"}
                          </span>
                        </div>

                        {/* Origen */}
                        <div className="flex justify-between bg-white/70 rounded-lg px-3 py-2">
                          <span className="text-gray-500">🚩 Origen</span>
                          <span className="font-semibold text-right">
                            {values.puertoOCiudadDeEmbarque || "—"}
                            {values.paísDeOrigen ? (
                              <span className="text-gray-400 font-normal">
                                {" "}
                                ({values.paísDeOrigen})
                              </span>
                            ) : null}
                          </span>
                        </div>

                        {/* Puerto de descarga (MARÍTIMO / MULTIMODAL) */}
                        {cfg.hasDischarge && (
                          <div className="flex justify-between bg-white/70 rounded-lg px-3 py-2">
                            <span className="text-gray-500">
                              ⚓ Puerto descarga
                            </span>
                            <span className="font-semibold text-right">
                              {values.puertoDeDescarga || "—"}
                              {values.paísDeDescarga ? (
                                <span className="text-gray-400 font-normal">
                                  {" "}
                                  ({values.paísDeDescarga})
                                </span>
                              ) : null}
                            </span>
                          </div>
                        )}

                        {/* Aeropuerto de arribo (AÉREO) */}
                        {cfg.hasArribo && (
                          <div className="flex justify-between bg-white/70 rounded-lg px-3 py-2">
                            <span className="text-gray-500">
                              ✈️ Aeropuerto arribo
                            </span>
                            <span className="font-semibold">
                              {values.aeropuertoDeArribo || "—"}
                            </span>
                          </div>
                        )}

                        {/* Destino final */}
                        {cfg.hasDestino && (
                          <div className="flex justify-between bg-white/70 rounded-lg px-3 py-2">
                            <span className="text-gray-500">
                              🏁 Destino final
                            </span>
                            <span className="font-semibold text-right">
                              {values.ciudadDestinoFinal || "—"}
                              {values.paísDestinoFinal ? (
                                <span className="text-gray-400 font-normal">
                                  {" "}
                                  ({values.paísDestinoFinal})
                                </span>
                              ) : null}
                            </span>
                          </div>
                        )}

                        {/* Días marítimos */}
                        {["MARITIMO", "MULTIMODAL"].includes(mode) && (
                          <div className="flex justify-between bg-white/70 rounded-lg px-3 py-2">
                            <span className="text-gray-500">
                              🌊 Días marítimos
                            </span>
                            <span className="font-semibold">
                              {values.díasMarítimos || "0"} días
                            </span>
                          </div>
                        )}

                        {/* Días aéreos */}
                        {mode === "AEREO" && (
                          <div className="flex justify-between bg-white/70 rounded-lg px-3 py-2">
                            <span className="text-gray-500">
                              ✈️ Días aéreos
                            </span>
                            <span className="font-semibold">
                              {values.díasAéreos || "0"} días
                            </span>
                          </div>
                        )}

                        {/* Días terrestres */}
                        {["TERRESTRE", "MULTIMODAL", "AEREO"].includes(
                          mode,
                        ) && (
                          <div className="flex justify-between bg-white/70 rounded-lg px-3 py-2">
                            <span className="text-gray-500">
                              🚛 Días terrestres
                            </span>
                            <span className="font-semibold">
                              {values.díasTerrestres || "0"} días
                            </span>
                          </div>
                        )}

                        {/* ETA */}
                        {["MARITIMO", "MULTIMODAL", "AEREO"].includes(mode) && (
                          <div className="flex justify-between bg-white/70 rounded-lg px-3 py-2">
                            <span className="text-gray-500">
                              {mode === "AEREO"
                                ? "✈️ ETA aeropuerto"
                                : "⚓ ETA puerto"}
                            </span>
                            <span className="font-semibold">
                              {values.eta || "—"}
                            </span>
                          </div>
                        )}

                        {/* Fecha devolución contenedores */}
                        {["MARITIMO", "MULTIMODAL"].includes(mode) && (
                          <div className="bg-white/70 rounded-lg px-3 py-2">
                            <div className="flex justify-between">
                              <span className="text-gray-500">
                                Fecha devolución contenedores
                              </span>
                              <span className="font-semibold">
                                {values.fechaDevoluciónContenedores || "—"}
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-blue-600">
                              ETA + 21 días.
                            </p>
                          </div>
                        )}

                        {/* Almacén */}
                        {["TERRESTRE", "MULTIMODAL", "AEREO"].includes(
                          mode,
                        ) && (
                          <div className="flex justify-between bg-white/70 rounded-lg px-3 py-2">
                            <span className="text-gray-500">
                              🏭 Llegada almacén
                            </span>
                            <span className="font-semibold">
                              {values.fechaDeLlegadaAAlmacen || "—"}
                            </span>
                          </div>
                        )}

                        {/* Total — ocupa todo el ancho */}
                        <div className="md:col-span-2 flex justify-between bg-blue-600 text-white rounded-xl px-4 py-3 mt-1">
                          <span className="font-bold">⏱ Total tránsito</span>
                          <span className="font-bold text-lg">
                            {values.totalTransitoCalculado || 0} días
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Navegación ── */}
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-100">
                <div className="w-32">
                  {activeStep > 1 && (
                    <button
                      type="button"
                      onClick={() => setActiveStep((s) => Math.max(s - 1, 1))}
                      className="px-6 py-2.5 font-semibold text-gray-700 bg-white hover:bg-gray-50 active:bg-gray-100 transition-all rounded-xl border border-gray-200 flex items-center gap-2 text-sm shadow-sm"
                    >
                      <i className="fas fa-arrow-left" /> Atrás
                    </button>
                  )}
                </div>

                <button
                  type="submit"
                  className="px-8 py-2.5 font-semibold text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98] transition-all rounded-xl shadow-md shadow-blue-100 flex items-center gap-2 text-sm"
                >
                  {activeStep === TOTAL_STEPS ? "Guardar" : "Siguiente"}{" "}
                  <i className="fas fa-arrow-right" />
                </button>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};
